<template>
    <div>
        <div v-if="!loaded || errorLoadingConversations" class="sc-launcher-inactive">
            <button
                v-show="!$isMobile"
                :style="{ backgroundColor: colors.launcher.bg }"
                class="sc-launcher"
                type="button"
                aria-label="Open chat"
                data-test="chat-launcher-inactive"
                @click="reloadConversations"
            >
                <e-tooltip multilined :label="tooltip">
                    <div class="sc-closed-icon">
                        <div v-if="errorLoadingConversations" class="is-new-message-count sc-badge">
                            <e-icon icon="exclamation" />
                        </div>
                        <e-icon :icon="chatIcon" :custom-class="conversationsLoading ? 'spin-fast' : ''" />
                    </div>
                </e-tooltip>
            </button>
        </div>
        <chat-widget
            v-else
            :participants="participants"
            :participants-loading="participantsLoading"
            :title-image-url="titleImageUrl"
            :on-message-was-sent="onMessageWasSent"
            :on-conversation-switch="onConversationSwitch"
            :message-list="messages"
            :messages-loading="messagesLoading"
            :message-history-count="messageHistoryCount"
            :current-conversation="currentConversation"
            :conversation-list="conversations"
            :new-messages-count="totalUnread"
            :online="online"
            :is-open="isChatOpen"
            :close="closeChat"
            :open="openChat"
            :show-emoji="false"
            :show-file="false"
            :show-typing-indicator="showTypingIndicator"
            :colors="colors"
            :always-scroll-to-bottom="alwaysScrollToBottom"
            :message-styling="messageStyling"
            :auth-user="user"
            :requested-conversation="confirmedConversationRequest"
            @typing="showTyping"
            @load-message-history="fetchMessageHistory"
            @switch-window="window => (currentWindow = window)"
        />
    </div>
</template>

<script>
import clone from 'lodash/clone.js';
import { createNamespacedHelpers } from 'vuex';

import Chat from '@/components/chat/system/Chat.vue';
import axios from '@/plugins/axios.js';
import { transformMessage } from '@/store/modules/chat/Conversation.js';
import { QUIZ_ATTEMPT_ROUTE_NAMES } from '@/utilities/sidebarMenuRoute.js';

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('Conversation');
const mapAuthGetters = createNamespacedHelpers('Auth').mapGetters;
const mapOnlineState = createNamespacedHelpers('Online').mapState;

export default {
    name: 'EdverumChat',

    components: { ChatWidget: Chat },

    data() {
        return {
            titleImageUrl: '',
            showTypingIndicator: null, // when set to a value matching the participant.id it shows the typing indicator for the specific user
            colors: {
                header: { bg: '#335CE5', text: '#FFFFFF' },
                progress: { bg: '#FFFFFF', fg: '#335CE5' },
                launcher: { bg: '#335CE5' },
                messageList: { bg: '#FFFFFF' },
                activeConversation: { bg: '#335CE5', text: '#FFFFFF' },
                conversationList: { bg: '#FFFFFF' },
                sentMessage: { bg: '#335CE5', text: '#FFFFFF' },
                receivedMessage: { bg: '#E5EFF5', text: '#222222' },
                userInput: { bg: '#E5EFF5', text: '#565867' },
            },
            alwaysScrollToBottom: true,
            messageStyling: true, // enables *bold* /emph/ _underline_ and such (more info at github.com/mattmezza/msgdown)
            chatChannel: null,
            currentWindow: 'conversations',
            notificationsListenerRegistered: false,
            /**
             * The requested conversation, set only once the chat has switched to it. The chat window
             * opens the message thread for this, so an unresolved request keeps the conversation list.
             */
            confirmedConversationRequest: null,
        };
    },

    computed: {
        ...mapAuthGetters(['user']),
        ...mapGetters(['conversations', 'messages', 'participants', 'loaded', 'totalUnread']),
        ...mapState([
            'isChatOpen',
            'conversationsLoading',
            'currentConversation',
            'participantsLoading',
            'messagesLoading',
            'errorLoadingConversations',
            'requestedConversation',
        ]),
        ...mapOnlineState(['online']),

        messageHistoryCount() {
            return this.$store.state.Conversation.messageCounts[this.currentConversation];
        },

        tooltip() {
            if (this.errorLoadingConversations) {
                return 'There was an error loading your conversations. Please click to try loading your conversations again.';
            }
            if (this.conversationsLoading) {
                return 'Loading conversations…';
            }
            if (this.totalUnread > 0) {
                const count = this.totalUnread;
                return count === 1 ? 'Open chat (1 unread message)' : `Open chat (${count} unread messages)`;
            }
            return 'Open chat';
        },

        chatIcon() {
            if (this.conversationsLoading) {
                return 'sync';
            }

            return this.errorLoadingConversations ? 'comment-info' : 'comment';
        },

        trimmedUser() {
            const user = clone(this.user);

            delete user.roles;

            return user;
        },
    },

    watch: {
        '$route.name': {
            immediate: true,
            handler(value) {
                if (!QUIZ_ATTEMPT_ROUTE_NAMES.includes(value) && value !== 'automated-survey-attempt') {
                    this.initializeConversations();
                }
            },
        },

        requestedConversation(request) {
            this.confirmedConversationRequest = null;

            if (request) {
                this.switchToRequestedConversation(request);
            }
        },

        loaded(isLoaded) {
            if (isLoaded && this.requestedConversation) {
                this.switchToRequestedConversation(this.requestedConversation);
            }
        },
    },

    beforeUnmount() {
        if (this.$echo) {
            this.$echo.leave(`chat-${this.currentConversation}`);

            if (this.user) {
                this.$echo.leave(`notifications-${this.user.id}`);
            }
        }
    },

    methods: {
        ...mapActions([
            'openLatestConversation',
            'addMessage',
            'sendMessage',
            'onMessageWasSent',
            'onConversationSwitch',
            'fetchMessageHistory',
            'openChat',
            'closeChat',
        ]),

        async initializeConversations() {
            if (this.$store.getters['Auth/check'] && !this.$store.getters['User/hasActiveAttempt']) {
                try {
                    // Also reload when a conversation was opened while the inbox never finished loading.
                    if (!this.currentConversation || !this.loaded) {
                        await this.openLatestConversation();
                    }

                    this.listenToConversation();

                    this.$store.commit('Conversation/SET_UNKNOWN_CONVERSATION_LISTENER', id => {
                        this.listenToConversation(id);
                    });

                    this.listenToNotifications();
                } catch (e) {
                    console.error(e);
                }
            }
        },

        listenToNotifications() {
            // The route watcher runs initializeConversations on every navigation, but the
            // per-user notifications channel must only be subscribed once. Re-subscribing would
            // stack duplicate ConversationMessageNotification handlers, causing the unread badge
            // to increment multiple times for a single incoming message.
            if (this.notificationsListenerRegistered) {
                return;
            }

            this.$echo
                .private(`notifications-${this.user.id}`)
                .listen('ConversationMessageNotification', async payload => {
                    const { data } = await axios.get(`/api/conversations/${payload.conversation.id}`);

                    this.$store.dispatch('Conversation/receiveNewMessage', {
                        conversation: data.data,
                        currentWindow: this.currentWindow,
                    });
                });

            this.notificationsListenerRegistered = true;
        },

        /**
         * Switch to a conversation another part of the app asked for. Before the conversations load,
         * this waits for the loaded watcher. A conversation missing from the loaded list is fetched
         * once, and the chat stays on the list if it still is not there. A request replaced while
         * this waits is dropped. Each request is a new object, so the check also tells apart two
         * requests for the same conversation.
         * @param request
         */
        async switchToRequestedConversation(request) {
            if (!this.loaded) {
                return;
            }

            const isCurrent = () => this.requestedConversation === request;
            const isListed = () => this.conversations.some(conversation => conversation.id === request.id);

            if (!isListed()) {
                await this.$store.dispatch('Conversation/fetchConversations');
            }

            if (!isCurrent() || !isListed()) {
                return;
            }

            await this.onConversationSwitch(request.id);

            if (isCurrent()) {
                this.confirmedConversationRequest = request;
            }
        },

        onConversationSwitch(conversationId) {
            this.$echo.leave(`chat-${this.currentConversation}`);

            const action = this.$store.dispatch('Conversation/onConversationSwitch', conversationId);

            this.listenToConversation();

            return action;
        },

        listenToConversation(conversationId) {
            const currentConversationId = conversationId || this.currentConversation;

            if (currentConversationId) {
                this.chatChannel = this.$echo.join(`chat-${currentConversationId}`);

                this.chatChannel
                    .listen('ConversationMessageSent', async e => {
                        this.showTypingIndicator = null;

                        const { data } = await axios.get(
                            `/api/conversations/${currentConversationId}/messages/${e.message.id}`
                        );

                        this.addMessage({
                            conversationId: e.conversation.id,
                            message: transformMessage(data.data),
                        });
                    })
                    .listenForWhisper('typing', e => {
                        this.showTypingIndicator = e.user.id;
                        setTimeout(() => (this.showTypingIndicator = null), 3000);
                    });
            }
        },

        showTyping() {
            setTimeout(() => {
                this.chatChannel.whisper('typing', {
                    user: this.trimmedUser,
                    typing: true,
                });
            }, 500);
        },

        reloadConversations() {
            if (this.errorLoadingConversations) {
                // Re-run the whole start-up: a failed first load also skipped the live listeners.
                return this.initializeConversations();
            }

            return undefined;
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-launcher {
    background-color: var(--primary-color) !important;
    border: 0;
    padding: 0;
    appearance: none;
    width: $size-1;
    height: $size-1;
    position: fixed;
    right: 1.5rem;
    // Lift above a page action bar (StickyActionBar) while one is mounted; unset elsewhere.
    bottom: calc(3.5rem + var(--page-action-bar-height, 0px));
    border-radius: 50%;
    box-shadow: none;
    transition: $transition-medium;
    cursor: pointer;
    display: grid;
    place-items: center;
    // Match Chat.vue's .sc-chat stacking so the launcher layers the same before and after conversations load.
    z-index: 1100;
}

.sc-launcher .sc-open-icon,
.sc-launcher .sc-closed-icon {
    color: $primary-invert;
    transition:
        opacity 100ms ease-in-out,
        transform 100ms ease-in-out;
}
</style>
